import React, { useEffect, useState } from 'react'
import {
  ActionIcon,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Text,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CounterClockwiseClockIcon,
} from '@radix-ui/react-icons'
import { AxiosError } from 'axios'

import { DayAvailabilityRow } from './DayAvailabilityRow'
import {
  copiedWeek,
  DayRanges,
  daysToRanges,
  DayValidationError,
  formatWeekLabel,
  nextWeek,
  previousWeek,
  rangesToDays,
  validateWeek,
  WeekRanges,
} from './weekUtils'

import { TimezoneSelect } from '@/components/TimezoneSelect'
import { useModals } from '@/hooks/useModals'
import {
  AvailabilityOwner,
  formatWeekStart,
  getWeeklyAvailabilityErrorMessage,
  useFetchWeeklyAvailability,
  useSetWeeklyAvailabilityMutation,
  useWeeklyAvailabilityQuery,
} from '@/queries/weeklyAvailability'

type WeekEditState = {
  // Null while the week has no timezone; the default only applies on display and save.
  timezone: string | null
  days: WeekRanges
  isReadOnly: boolean
}

export const WeeklyAvailabilityModal: React.FC<{
  owner: AvailabilityOwner
  defaultTimezone: string
  initialWeekStart: Date
  onClose: () => void
}> = ({ owner, defaultTimezone, initialWeekStart, onClose }) => {
  const [weekStart, setWeekStart] = useState(initialWeekStart)
  const [edits, setEdits] = useState<Record<string, WeekEditState>>({})
  const [errors, setErrors] = useState<DayValidationError[]>([])
  const [isCopying, setIsCopying] = useState(false)

  const weekKey = formatWeekStart(weekStart)
  const current = edits[weekKey]

  const goToWeek = (nextWeekStart: Date) => {
    setErrors([])
    setWeekStart(nextWeekStart)
  }

  const { data: response, isLoading } = useWeeklyAvailabilityQuery(
    owner,
    weekStart,
  )

  useEffect(() => {
    if (response && !edits[weekKey]) {
      const data = response.data
      setEdits(prev => ({
        ...prev,
        [weekKey]: {
          timezone: data.timezone ?? null,
          days: rangesToDays(data.ranges ?? []),
          isReadOnly: data.is_read_only,
        },
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response, weekKey])

  const { mutateAsync: save, isLoading: isSaving } =
    useSetWeeklyAvailabilityMutation()
  const fetchWeeklyAvailability = useFetchWeeklyAvailability()
  const { openConfirmModal } = useModals()

  const updateCurrent = (update: Partial<WeekEditState>) => {
    setEdits(prev => {
      const existing = prev[weekKey]
      if (!existing) {
        return prev
      }
      return { ...prev, [weekKey]: { ...existing, ...update } }
    })
  }

  const onDayChange = (weekday: number, ranges: DayRanges) => {
    if (!current) {
      return
    }
    setErrors(prev => prev.filter(error => error.weekday !== weekday))
    const days = current.days.slice() as WeekRanges
    days[weekday] = ranges
    updateCurrent({ days })
  }

  const onSave = async () => {
    if (!current) {
      return
    }

    const validationErrors = validateWeek(current.days)
    if (validationErrors.length > 0) {
      setErrors(validationErrors)
      return
    }

    const timezone = current.timezone ?? defaultTimezone

    try {
      const { data } = await save({
        owner,
        data: {
          week_start: weekKey,
          timezone,
          ranges: daysToRanges(current.days),
        },
      })
      setEdits(prev => ({
        ...prev,
        [weekKey]: {
          timezone: data.timezone ?? timezone,
          days: rangesToDays(data.ranges ?? []),
          isReadOnly: data.is_read_only,
        },
      }))
      showNotification({ message: 'Availability saved' })
    } catch (error) {
      showNotification({
        color: 'red',
        message: getWeeklyAvailabilityErrorMessage(
          error as AxiosError<{ detail?: unknown }>,
          'Could not save availability. Please try again.',
        ),
      })
    }
  }

  const applyCopyLastWeek = async () => {
    const previousWeekKey = formatWeekStart(previousWeek(weekStart))

    setIsCopying(true)
    try {
      const previous =
        edits[previousWeekKey] ??
        (await fetchWeeklyAvailability(owner, previousWeek(weekStart)).then(
          ({ data }) => ({
            days: rangesToDays(data.ranges ?? []),
            timezone: data.timezone,
          }),
        ))
      setErrors([])
      updateCurrent(copiedWeek(previous))
    } catch {
      showNotification({
        color: 'red',
        message: "Could not load last week's hours. Please try again.",
      })
    } finally {
      setIsCopying(false)
    }
  }

  const onCopyLastWeek = () => {
    if (!current) {
      return
    }

    const hasExistingHours = current.days.some(dayRanges =>
      dayRanges.some(range => range.start || range.end),
    )

    if (!hasExistingHours) {
      void applyCopyLastWeek()
      return
    }

    openConfirmModal({
      title: "Replace this week's hours?",
      children: (
        <Text size="sm">
          Copying last week&apos;s hours will replace what you&apos;re currently
          viewing for this week. This can&apos;t be undone.
        </Text>
      ),
      labels: { confirm: 'Replace hours', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: applyCopyLastWeek,
    })
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title="Available working hours"
      size="lg"
      centered
      closeOnEscape={false}
    >
      <Stack>
        <Text size="sm" color="dimmed">
          Set working hours to let Operations know what times you&apos;re
          working and when you&apos;re available to book events.
        </Text>

        <TimezoneSelect
          label="Timezone"
          value={current?.timezone ?? defaultTimezone}
          disabled={!current || current.isReadOnly}
          onChange={value => value && updateCurrent({ timezone: value })}
          referenceDate={weekStart}
        />

        <Group position="apart">
          <Group spacing="xs">
            <ActionIcon
              variant="default"
              aria-label="Previous week"
              onClick={() => goToWeek(previousWeek(weekStart))}
            >
              <ChevronLeftIcon />
            </ActionIcon>
            <Text size="sm" weight={500} className="min-w-[220px] text-center">
              {formatWeekLabel(weekStart)}
            </Text>
            <ActionIcon
              variant="default"
              aria-label="Next week"
              onClick={() => goToWeek(nextWeek(weekStart))}
            >
              <ChevronRightIcon />
            </ActionIcon>
          </Group>
          <Button
            variant="default"
            size="xs"
            leftIcon={<CounterClockwiseClockIcon />}
            onClick={onCopyLastWeek}
            loading={isCopying}
            disabled={!current || current.isReadOnly}
          >
            Copy last week
          </Button>
        </Group>

        {isLoading && !current ? (
          <Group position="center" py="xl">
            <Loader size="sm" />
          </Group>
        ) : (
          current && (
            <Stack spacing="sm">
              {current.isReadOnly && (
                <Text size="xs" color="dimmed">
                  This week has already passed and can no longer be edited.
                </Text>
              )}
              {current.days.map((ranges, weekday) => (
                <DayAvailabilityRow
                  key={weekday}
                  weekday={weekday}
                  ranges={ranges}
                  readOnly={current.isReadOnly}
                  errors={Object.fromEntries(
                    errors
                      .filter(error => error.weekday === weekday)
                      .map(error => [error.rangeIndex, error.message]),
                  )}
                  onChange={updated => onDayChange(weekday, updated)}
                />
              ))}
            </Stack>
          )
        )}

        <Group position="right" mt="md">
          <Button variant="default" onClick={onClose}>
            Discard
          </Button>
          {!current?.isReadOnly && (
            <Button onClick={onSave} loading={isSaving} disabled={!current}>
              Save
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  )
}
